import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { apiService } from '../../../shared/services/api'

interface VerifyResponse {
  success: boolean
  message: string
  data?: {
    originalUrl: string
    requiresPassword: boolean
  }
  code?: string
}

const PasswordVerifyPage: React.FC = () => {
  const { t } = useTranslation()
  const { shortCode } = useParams<{ shortCode: string }>()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!shortCode) {
      navigate('/')
    }
  }, [shortCode, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) {
      setError(t('verify.required'))
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await apiService.client.post<VerifyResponse>(
        `/urls/${shortCode}/verify`,
        { password }
      )
      const data = response.data

      if (data.success && data.data?.originalUrl) {
        // Success - redirect to original URL
        toast.success(t('verify.success'))
        window.location.href = data.data.originalUrl
      } else {
        // Handle different error cases
        switch (data.code) {
          case 'INVALID_PASSWORD':
            setError(t('verify.invalidPassword'))
            break
          case 'URL_NOT_FOUND':
            setError(t('verify.notFound'))
            break
          case 'URL_NOT_ACCESSIBLE':
            setError(t('verify.unavailable'))
            break
          default:
            setError(data.message || t('verify.genericError'))
        }
      }
    } catch (error) {
      console.error('Password verification error:', error)
      setError(t('verify.networkError'))
    } finally {
      setLoading(false)
    }
  }

  if (!shortCode) {
    return null
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
              <LockClosedIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              🔒 {t('verify.title')}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              {t('verify.description')}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('verify.codeLabel')}:{' '}
              <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs dark:bg-gray-700">
                {shortCode}
              </code>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('verify.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('verify.passwordPlaceholder')}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  required
                  autoFocus
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password.trim()}
              className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t('verify.submitting')}
                </div>
              ) : (
                t('verify.submit')
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {t('verify.home')}
            </button>
          </div>

          {/* Info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('verify.hint')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordVerifyPage
